import * as React from 'react';

import { TicketLineDTO, Res, SoldProductType, TicketLineStatus } from 'core/api';
import { Result } from 'core/infrastructure';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { CustomerDisplay } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { Box, VStack } from '@chakra-ui/react';
import * as Images from 'pos/Images';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen } from 'pos/ui';
import { MoreActionsGrid } from 'pos/ui/MoreActionsButton';

type EditLineProps = {
	line: TicketLineDTO,
	showAddNotesOrEditMenu: boolean,
	showChangeAddins: boolean,
	showAdjustIngredients: boolean,
	showSetPreparationOrder: boolean,
	showRemoveLine: boolean,
	showSetTicketLineCreator: boolean,
	showPrepare: boolean,
	showRequest: boolean,
	showSetPrice: boolean,
	showSetQuantity: boolean,
	showSetDiscounts: boolean,
	showProductDetails: boolean,
	showEditSeatNumber: boolean,
}

type Props = EditLineProps & Modal.Actions<() => Promise<Result>>;

function EditTicketLineDialog(props: Props): React.JSX.Element {
	const { cancel, line, close,
		showAddNotesOrEditMenu,
		showChangeAddins,
		showAdjustIngredients,
		showSetPreparationOrder,
		showRemoveLine,
		showSetTicketLineCreator,
		showSetQuantity,
		showSetPrice,
		showSetDiscounts,
		showPrepare,
		showRequest,
		showEditSeatNumber,
		showProductDetails } = props;

	const getAddNotesOrEditMenuText = (): string => {
		return line.Type === SoldProductType.MenuHeader
			? Res.Web.EDIT_TICKET_LINE_EDIT_MENU_LBL()
			: Res.Web.EDIT_TICKET_LINE_ADD_NOTES_LBL();
	};

	const getAddNotesOrEditMenuIcon = (): SvgIcon => {
		return line.Type === SoldProductType.MenuHeader
			? Images.ComposeMenu
			: Images.GetNotes;
	};

	const removeTicketLine = async () => {
		const action = async () => await Command.execute('removeTicketLine', line);
		close(action);
	};

	const setLinePreparationOrder = async () => {
		const action = async () => await Command.execute('setLinePreparationOrder', line);
		close(action);
	};

	const setTicketLineAddins = async () => {
		const action = async () => await Command.execute('setTicketLineAddins', line);
		close(action);
	};

	const adjustIngredients = async () => {
		const action = async () => await Command.execute('adjustTicketLineIngredients', line);
		close(action);
	};

	const prepare = async () => {
		const action = async () => await Command.execute('prepareTicketLine', line);
		close(action);
	};

	const prepareAgain = async () => {
		const action = async () => await Command.execute('prepareTicketLineAgain', line);
		close(action);
	};

	const request = async () => {
		const action = async () => await Command.execute('requestTicketLine', line);
		close(action);
	};

	const addTicketLineNoteOrEditMenu = async (): Promise<void> => {
		if (!line) return;

		if (line.Type === SoldProductType.MenuHeader) {
			const action = async () => Command.execute('composeMenu', line, 'edition');
			close(action);
			return;
		}

		if (line.Status !== TicketLineStatus.New) {
			await Message.show(Res.Msg.MSG_LINE_HAS_ALREADY_BEEN_ORDERED());
			return;
		}

		const action = async () => await Command.execute('addTicketLineNote', line);
		close(action);
	};

	const setQuantity = async (): Promise<void> => {
		const action = async () => await Command.execute('setTicketLineQuantity', line);
		close(action);
	};

	const setPrice = async (): Promise<void> => {
		const action = async () => await Command.execute('setTicketLinePrice', line);
		close(action);
	};

	const setLineDiscount = async (): Promise<void> => {
		const action = async () => await Command.execute('setTicketLineDiscount', line);
		close(action);
	};

	const showLineProductDetails = async () => {
		const action = async () => await Command.execute('showProductDetails', line.Product, SalesStore.getLocation());
		close(action);
	};

	const editLineSeatNumber = async () => {
		const action = async () => await Command.execute('editLineSeatNumber', line);
		close(action);
	};

	const setTicketLineCreator = async () => {
		const action = async () => await Command.execute('setTicketLineCreator', line);
		close(action);
	};

	const prepareButton =
		line.Status === TicketLineStatus.New
			? { title: Res.Web.EDIT_TICKET_LINE_PREPARE_LBL(), image: Images.PrepareTicketLine, action: prepare }
			: { title: Res.Web.EDIT_TICKET_LINE_PREPARE_AGAIN_LBL(), image: Images.PrepareTicketLine, action: prepareAgain };

	const requestButton =
		line.Status !== TicketLineStatus.New && line.PreparationOrderId > 0 && line.PreparationOrderCanBeRequested
			? { title: Res.Web.EDIT_TICKET_LINE_REQUEST_LBL(), image: Images.RequestOrderedTicketLine, action: request }
			: null;

	const removeButton =
		line.Status === TicketLineStatus.New
			? { title: Res.Web.EDIT_TICKET_LINE_REMOVE_LBL(), image: Images.DeleteTicketLine, action: removeTicketLine }
			: { title: Res.Web.EDIT_TICKET_LINE_CANCEL_LBL(), image: Images.CancelTicketLine, action: removeTicketLine };

	const editSeatNumberButton =
		line.Status === TicketLineStatus.New
			? { title: Res.Web.EDIT_TICKET_LINE_SEAT_NUMBER_LBL(), image: Images.EditLineSeatNumber, action: editLineSeatNumber }
			: null;

	return <PosScreen screenId='EDIT_TICKET_LINE' size={{ height: 460, width: 540 }}>

		<Header title={Res.Web.EDIT_TICKET_LINE()} onClose={cancel} />

		<Content>
			<VStack gap={'6'} justifyContent={'center'} h='full' w='full'>
				<Box fontSize='md' fontWeight='bold' >
					{Res.Web.EDIT_TICKET_LINE_SELECT_OPTION_LBL()}
				</Box>
				<MoreActionsGrid surface='edit-ticket-line-modal' rows={6} columns={2} buttons={[
					showAddNotesOrEditMenu
						? { title: getAddNotesOrEditMenuText(), image: getAddNotesOrEditMenuIcon(), action: addTicketLineNoteOrEditMenu }
						: null,
					showChangeAddins
						? { title: Res.Web.EDIT_TICKET_LINE_CHANGE_ADDINS_LBL(), image: Images.ComposeProduct, action: setTicketLineAddins }
						: null,
					showSetTicketLineCreator
						? { title: Res.Web.SET_TICKET_LINE_CREATOR_ACTION_BTN(), image: Images.ChangeTicketOpenerUser, action: setTicketLineCreator }
						: null,
					showAdjustIngredients
						? { title: Res.Web.EDIT_TICKET_LINE_ADJUST_INGREDIENTS_LBL(), image: Images.AdjustIngredients, action: adjustIngredients }
						: null,
					showSetPreparationOrder
						? { title: Res.Web.EDIT_TICKET_LINE_SET_PREPARATION_ORDER_LBL(), image: Images.ChangeDefaultPreparationOrder, action: setLinePreparationOrder }
						: null,
					showSetQuantity
						? { title: Res.Web.EDIT_TICKET_LINE_QUANTITY_LBL(), image: Images.EditQuantity, action: setQuantity }
						: null,
					showProductDetails
						? { title: Res.Web.EDIT_TICKET_LINE_DETAILS_LBL(), image: Images.ProductDataSheet, action: showLineProductDetails }
						: null,
					showSetDiscounts
						? { title: Res.Web.EDIT_TICKET_LINE_SET_DISCOUNT_LBL(), image: Images.SetTicketLineDiscount, action: setLineDiscount }
						: null,
					showEditSeatNumber ? editSeatNumberButton : null,
					showRemoveLine ? removeButton : null,
					showPrepare ? prepareButton : null,
					showSetPrice
						? { title: Res.Web.EDIT_TICKET_LINE_PRICE_LBL(), image: Images.Euro, action: setPrice }
						: null,
					showRequest ? requestButton : null
				]}>

				</MoreActionsGrid>
			</VStack>
		</Content>


		<Footer>
			<PhoneOnly>
				<BackActionButton action={async () => { cancel(); }} />
			</PhoneOnly>
		</Footer>

	</PosScreen >;
}

async function editTicketLine(line: TicketLineDTO,
	showParams: Omit<EditLineProps, 'line' | 'actions'>
): Promise<Result> {

	const result = await Modal.show(EditTicketLineDialog, { line, ...showParams });

	if (!result.ok)
		return Result.error();

	await result.value();

	await CustomerDisplay.displayExistingLine(SalesStore.getTicket(), SalesStore.getLineById(line.Id));

	return Result.ok();
}

declare global {
	interface CommandType {
		editTicketLine: (line: TicketLineDTO, params: Omit<EditLineProps, 'line' | 'actions'>) => Promise<Result>
	}
}

Command.register('editTicketLine', 'NO_PRECONDITION', editTicketLine);